/**
 * @file project/importer.ts
 * @description Logic for importing various image formats into the active project.
 */

import { g, layers, LayerClass, newDocument } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { imageFormatRegistry } from '../format-registry';
import { ProjectSerializer } from './serializer';

export class ProjectImporter {
  static async importImage(buffer: ArrayBuffer, fileName: string): Promise<boolean> {
    console.log(`[Importer] Importing ${fileName}...`);
    const ext = "." + fileName.split(".").pop()?.toLowerCase();
    
    if (ext === ".hcie") {
        const decoder = new TextDecoder();
        const json = decoder.decode(buffer);
        return await ProjectSerializer.loadProject(json);
    }

    const format = imageFormatRegistry.getByExtension(ext);
    if (!format || !format.canRead) {
        throw new Error(`Unsupported or unreadable format: ${ext}`);
    }

    const decodedImage = await format.read(buffer);
    if (!decodedImage || !decodedImage.layers || decodedImage.layers.length === 0) {
        throw new Error(`The format adapter for ${ext} returned no valid image data.`);
    }

    // Initialize document with image size from DecodedImage
    newDocument(fileName, decodedImage.width, decodedImage.height);

    layers.length = 0;
    for (let i = 0; i < decodedImage.layers.length; i++) {
        const layerData = decodedImage.layers[i];
        const layerName = layerData.name || `Layer ${i + 1}`;
        const canvasOrData = layerData.canvas;
        
        let width = canvasOrData.width;
        let height = canvasOrData.height;

        const layer = new LayerClass(layerName, width, height);
        
        layer.visible = layerData.visible !== false;
        layer.opacity = layerData.opacity ?? 1.0;
        layer.blendMode = (layerData.blendMode as any) || 'source-over';
        (layer as any).x = layerData.x ?? 0;
        (layer as any).y = layerData.y ?? 0;

        if (canvasOrData instanceof ImageData) {
            (layer.ctx as any).putImageData(canvasOrData, 0, 0);
        } else {
            (layer.ctx as any).drawImage(canvasOrData, 0, 0);
        }
        layers.push(layer);
    }

    g.activeLayerIndex = layers.length - 1;
    
    // Notify UI
    EventBus.emit('PROJECT_RESIZE', { width: g.image_width, height: g.image_height });
    EventBus.emit('PROJECT_LOADED', { layerCount: layers.length });
    
    console.log(`[Importer] Successfully imported ${fileName}. Layers: ${layers.length}`);
    return true;
  }
}
